import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import useStore from "../store/useStore";
import api from "../services/api";
import AuthShell, {
  AuthMessage,
  AuthTabs,
  PasswordVisibilityButton,
  Spinner,
  authInputClass,
  authLabelClass,
  authLinkClass,
  authPrimaryButtonClass
} from "../components/auth/AuthUI";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, toast, isAuthenticated } = useStore();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });

  useEffect(() => {
    if (location.state?.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
      toast("Account created! Please login with your credentials.", "success");
    }
  }, [location.state, toast]);

  const handleChange = (e) => {
    if (errorMessage) {
      setErrorMessage("");
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setErrorMessage("Please enter email and password");
      toast("Please enter email and password", "error");
      return;
    }

    try {
      setErrorMessage("");
      setLoading(true);
      const response = await api.post("/auth/login", formData);

      if (response.data.success) {
        if (typeof login !== 'function') {
          toast("Store error. Please refresh the page.", "error");
          return;
        }
        login(response.data.user, response.data.token);
        toast(`Welcome back, ${response.data.user.firstName}!`, "success");
        setTimeout(() => navigate("/dashboard"), 500);
      }
    } catch (error) {
      console.error("❌ Login error:", error);
      const message = error.response?.data?.message || "Invalid credentials";
      setErrorMessage(message);
      toast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthShell
      subtitle="Sign in to access your rental dashboard"
      footnote="Use the email you signed up with. Contact Gamrent admin if your dashboard has not been activated yet."
    >
      <AuthTabs active="login" onSignIn={() => {}} onSignUp={() => navigate("/signup")} />

      {errorMessage ? <AuthMessage>{errorMessage}</AuthMessage> : null}

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Email */}
        <div>
          <label htmlFor="login-email" className={authLabelClass}>
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="login-email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            required
            autoComplete="email"
            className={authInputClass}
          />
        </div>

        {/* Password */}
        <div>
          <label htmlFor="login-password" className={authLabelClass}>
            Password <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              required
              autoComplete="current-password"
              className={`${authInputClass} pr-12`}
            />
            <PasswordVisibilityButton visible={showPassword} onToggle={() => setShowPassword(!showPassword)} />
          </div>
        </div>

        {/* Forgot Password */}
        <div className="-mt-1">
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className={authLinkClass}
          >
            Forgot password?
          </button>
        </div>

        {/* Submit */}
        <button type="submit" disabled={loading} className={authPrimaryButtonClass}>
          {loading ? (
            <>
              <Spinner />
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </form>
    </AuthShell>
  );
};

export default Login;